import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import * as pdfjsLib from "pdfjs-dist";

import { tokens } from "../../../theme";
import { DownloadIcon } from "../../../components/icons";
import { CrewDocumentRecord, getDocumentName } from "../types/crew-documents";
import {
  downloadDocument,
  getPreviewKind,
  openDocument,
  toSameOriginUrl,
} from "../utils/crewDocuments";

type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>["promise"]>;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/**
 * Other modules pin the worker to a hard-coded CDN version that no longer
 * matches the installed `pdfjs-dist`, and those assignments run at import time.
 * Setting it here — right before each load — guarantees the worker matches the
 * library actually doing the parsing, whatever import order webpack picks.
 */
const ensurePdfWorker = () => {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
};

// ── PDF preview ────────────────────────────────────────────────────────────

interface PdfPreviewProps {
  url: string;
  zoom: number;
  onLoaded: (pageCount: number) => void;
  pageNumber: number;
}

const PdfPreview: React.FC<PdfPreviewProps> = ({
  url,
  zoom,
  onLoaded,
  pageNumber,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

  const [pdf, setPdf] = useState<PdfDocument | null>(null);
  const [loading, setLoading] = useState(true);
  // Set when pdf.js can't read the file (usually a CORS-blocked media host) —
  // the browser's own PDF plugin often still can, so we hand it the URL.
  const [fallbackToIframe, setFallbackToIframe] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    setContainerWidth(element.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.contentRect.width),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loaded: PdfDocument | null = null;

    setLoading(true);
    setFallbackToIframe(false);
    setPdf(null);
    ensurePdfWorker();

    const loadingTask = pdfjsLib.getDocument({
      url,
      withCredentials: false,
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
      cMapPacked: true,
    });

    loadingTask.promise
      .then((document) => {
        loaded = document;
        if (cancelled) {
          document.destroy();
          return;
        }
        setPdf(document);
        onLoaded(document.numPages);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("pdf.js could not render the document:", error);
        setFallbackToIframe(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      loaded?.destroy();
    };
  }, [url, onLoaded]);

  useEffect(() => {
    let cancelled = false;

    const renderPage = async () => {
      const canvas = canvasRef.current;
      if (!pdf || !canvas || fallbackToIframe || containerWidth === 0) return;

      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        // Fit the page to the dialog, then apply the user's zoom on top.
        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = containerWidth / baseViewport.width;
        const viewport = page.getViewport({ scale: fitScale * zoom });

        // Render at device pixel density so text stays sharp on retina screens.
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        renderTaskRef.current?.cancel();
        const task = page.render({
          canvas,
          viewport,
          transform:
            outputScale !== 1
              ? [outputScale, 0, 0, outputScale, 0, 0]
              : undefined,
        });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) setLoading(false);
      } catch (error) {
        // A cancelled render is expected whenever the page or zoom changes.
        if ((error as { name?: string })?.name === "RenderingCancelledException")
          return;
        if (cancelled) return;
        console.warn("Failed to render PDF page:", error);
        setFallbackToIframe(true);
        setLoading(false);
      }
    };

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [pdf, pageNumber, zoom, containerWidth, fallbackToIframe]);

  if (fallbackToIframe) {
    return (
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1,
            mb: 1,
            borderRadius: 1,
            bgcolor: "warning.light",
            color: "warning.contrastText",
          }}
        >
          <Typography variant="caption">
            The file couldn&apos;t be read directly — showing the browser&apos;s
            own preview. If it stays blank, the file host is blocking embedding.
          </Typography>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => openDocument(url)}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Open in new tab
          </Button>
        </Box>
        <iframe
          src={url}
          title={getDocumentName(url)}
          style={{ border: "none", width: "100%", flex: 1 }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        position: "relative",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
    </Box>
  );
};

// ── Image preview ──────────────────────────────────────────────────────────

const ImagePreview: React.FC<{ url: string; zoom: number }> = ({
  url,
  zoom,
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [url]);

  if (failed) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ m: "auto" }}>
        This image could not be loaded.
      </Typography>
    );
  }

  const zoomedIn = zoom > 1;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        // Unlike a PDF, an image has no pages to scroll through — at or below
        // 100% it is sized to fit the dialog, so only zooming in scrolls.
        overflow: zoomedIn ? "auto" : "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <img
        src={url}
        alt={getDocumentName(url)}
        onError={() => setFailed(true)}
        style={
          zoomedIn
            ? { width: `${zoom * 100}%`, height: "auto", maxWidth: "none" }
            : {
                maxWidth: `${zoom * 100}%`,
                maxHeight: `${zoom * 100}%`,
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }
        }
      />
    </Box>
  );
};

// ── Dialog ─────────────────────────────────────────────────────────────────

export interface DocumentViewerDialogProps {
  open: boolean;
  onClose: () => void;
  /** The full list, so the viewer can page between documents. */
  documents: CrewDocumentRecord[];
  activeIndex: number;
  onNavigate: (index: number) => void;
}

export const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  open,
  onClose,
  documents,
  activeIndex,
  onNavigate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [zoom, setZoom] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(0);

  const record = documents[activeIndex];
  const previewKind = record ? getPreviewKind(record.document) : "unsupported";

  useEffect(() => {
    setZoom(1);
    setPageNumber(1);
    setPageCount(0);
  }, [record?.id, open]);

  const handleLoaded = useCallback((count: number) => setPageCount(count), []);

  if (!record) return null;

  const fileName = getDocumentName(record.document);
  // Same-origin in dev (via the /media proxy), untouched in production.
  const previewUrl = toSameOriginUrl(record.document);
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < documents.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="document-viewer-title"
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        id="document-viewer-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600} noWrap title={fileName}>
              {fileName}
            </Typography>
            <Chip
              label={record.type_name}
              size="small"
              sx={{ borderRadius: 1, flexShrink: 0 }}
            />
          </Box>
          {documents.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              Document {activeIndex + 1} of {documents.length}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                disabled={previewKind === "unsupported" || zoom <= MIN_ZOOM}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" sx={{ width: 44, textAlign: "center" }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                disabled={previewKind === "unsupported" || zoom >= MAX_ZOOM}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Download">
            <IconButton onClick={() => downloadDocument(record.document)}>
              <DownloadIcon color="info" sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <IconButton onClick={() => openDocument(record.document)}>
              <OpenInNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2,
          bgcolor: colors.primary[900],
        }}
      >
        {previewKind === "pdf" && (
          <PdfPreview
            url={previewUrl}
            zoom={zoom}
            pageNumber={pageNumber}
            onLoaded={handleLoaded}
          />
        )}

        {previewKind === "image" && <ImagePreview url={previewUrl} zoom={zoom} />}

        {previewKind === "unsupported" && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              textAlign: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              This file type can&apos;t be previewed here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Word and other office formats need to be downloaded to be read.
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                onClick={() => downloadDocument(record.document)}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => openDocument(record.document)}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Open in new tab
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <Button
          size="small"
          startIcon={<ChevronLeftIcon />}
          disabled={!hasPrevious}
          onClick={() => onNavigate(activeIndex - 1)}
          sx={{ textTransform: "none" }}
        >
          Previous document
        </Button>

        {previewKind === "pdf" && pageCount > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((page) => page - 1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption">
              Page {pageNumber} of {pageCount}
            </Typography>
            <IconButton
              size="small"
              disabled={pageNumber >= pageCount}
              onClick={() => setPageNumber((page) => page + 1)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          disabled={!hasNext}
          onClick={() => onNavigate(activeIndex + 1)}
          sx={{ textTransform: "none" }}
        >
          Next document
        </Button>
      </Box>
    </Dialog>
  );
};

export default DocumentViewerDialog;
